import { ColumnDef, Row, Cell } from "@tanstack/react-table";
import { RegistrantRow } from "../types";
import { getStickyColumnClasses } from "@/lib/styleHelpers";
import type { Action } from "@/components/MenuButton";
import Checkbox from "@/components/Checkbox";
import Icon from "@/components/Icon";
import RegistrantStatusCell from "./RegistrantStatusCell";
import { isExcessiveScore } from "../_utils";
import SelectAllHeaderCheckbox from "./SelectAllHeaderCheckbox";
import {
  buildIconTextColumn,
  buildAvatarDetailsColumn,
  buildSegmentedButtonColumn,
  buildActionColumn
} from "@/components/Table";
import { getPATDocumentIconConfig } from "@/features/person/utils/pat";
import { actionId } from "@/hooks/useTableRowMenu";
import React from "react";

export const showHeaderInColumn = "show-header-in-column";

const getDetailsColumnText = (row: RegistrantRow): React.ReactNode => {
  if (row.note) {
    return <span>{row.note}</span>;
  }

  return <span className="opacity-20">--</span>;
};

interface GetColumnsProps {
  hasRegistrations: boolean;
  setMenu: (options: { rowId: string; actions: Action[] }) => void;
  setUpdateStatusDialogOpen: (open: boolean) => void;
  onAttendanceUpdate?: (id: string, value: string | null) => void;
  onScoreUpdate?: (field: string, id: string, value: string) => void;
  setTargetEventRegistration: (targetEventRegistration: RegistrantRow) => void;
  handleStatusUpdate: (status: string | null, note: string | null, id: string | null) => void;
  isFinalized: boolean;
  canEdit: boolean;
  setShowEditScoreModal: (open: boolean) => void;
  isCompactLayout: boolean;
  isSetupComplete: boolean;
  setSelectedPgaId: (pgaId: string | null) => void;
  setIsPersonSideSheetOpen: (open: boolean) => void;
  onEditAttendanceClick?: (row: RegistrantRow) => void;
}

const getAttendanceCompactLabel = (row: RegistrantRow) => {
  if (row.attended === true) return "Present";
  if (row.attended === false) return "Absent";
  return "--";
};

const getRowActions = ({
  row,
  isSetupComplete,
  canEdit,
  isFinalized,
  setShowEditScoreModal,
  setTargetEventRegistration,
  setUpdateStatusDialogOpen,
  handleStatusUpdate
}: {
  row: RegistrantRow;
  isSetupComplete: boolean;
  canEdit: boolean;
  isFinalized: boolean;
  setShowEditScoreModal: (open: boolean) => void;
  setTargetEventRegistration: (targetEventRegistration: RegistrantRow) => void;
  setUpdateStatusDialogOpen: (open: boolean) => void;
  handleStatusUpdate: (status: string | null, note: string | null, id: string | null) => void;
}): Action[] => {
  if (!isSetupComplete || !canEdit) {
    return [];
  }

  const { score_total, attended, additionalStatus } = row;

  if (isFinalized) {
    return [
      {
        text: "Edit PAT Score",
        onClick: () => {
          setShowEditScoreModal(true);
          setTargetEventRegistration(row);
        }
      }
    ];
  }

  if (((attended && !isExcessiveScore(score_total)) || !attended) && additionalStatus !== "late_cancel") {
    return [
      {
        text: "Update Status",
        onClick: () => {
          setUpdateStatusDialogOpen(true);
          setTargetEventRegistration(row);
        }
      },
      {
        text: "Reset Status",
        onClick: () => {
          handleStatusUpdate(null, null, row.id);
        }
      }
    ];
  }

  return [];
};

export const getColumns = ({
  hasRegistrations,
  setMenu,
  setUpdateStatusDialogOpen,
  onAttendanceUpdate,
  onScoreUpdate,
  setTargetEventRegistration,
  handleStatusUpdate,
  isFinalized,
  canEdit,
  setShowEditScoreModal,
  isCompactLayout,
  isSetupComplete,
  setSelectedPgaId,
  setIsPersonSideSheetOpen,
  onEditAttendanceClick
}: GetColumnsProps): ColumnDef<RegistrantRow, any>[] => {
  const participantColumns = buildAvatarDetailsColumn<RegistrantRow>({
    isCompactLayout,
    id: "participant",
    header: "Participant",
    getTitle: (row) => row.registrantName,
    getSubtitle: (row) => row.programType,
    getPicture: (row) => row.profilePhoto || undefined,
    sortType: "text",
    size: 200
  });

  if (!isCompactLayout && participantColumns[0]) {
    participantColumns[0] = {
      ...participantColumns[0],
      meta: {
        ...participantColumns[0].meta,
        className: `${getStickyColumnClasses(hasRegistrations)} z-10 ${!isFinalized ? "pl-2" : ""}`,
        headerClassName: `${!isFinalized ? "-ml-2" : ""}`
      }
    };
  }

  const attendanceColumn: ColumnDef<RegistrantRow, unknown> = isSetupComplete
    ? buildSegmentedButtonColumn<RegistrantRow>({
        id: "attendance",
        header: "Attendance",
        options: [
          { label: "Present", value: "true" },
          { label: "Absent", value: "false" }
        ],
        getValue: (row) => (row.attended === null ? null : row.attended ? "true" : "false"),
        onChange: (row, value) => {
          const currentValue = row.attended === null ? null : row.attended ? "true" : "false";
          if (value === currentValue) {
            onAttendanceUpdate?.(row.sessionRegistrationId, null);
          } else {
            onAttendanceUpdate?.(row.sessionRegistrationId, value);
          }
        },
        disabled: isFinalized || !isSetupComplete,
        compactLabel: getAttendanceCompactLabel,
        meta: {
          headerClassName: showHeaderInColumn
        }
      })
    : {
        id: "attendance",
        header: "Attendance",
        enableSorting: false,
        cell: () => <span className="opacity-20">--</span>,
        meta: {
          headerClassName: showHeaderInColumn
        }
      };

  return [
    ...(!isFinalized
      ? [
          {
            id: "select",
            header: ({ table }: { table: any }) => <SelectAllHeaderCheckbox table={table} isFinalized={isFinalized} />,
            cell: ({ row }: { row: Row<RegistrantRow> }) => (
              <Checkbox
                name={`select-row-${row.id}`}
                checked={row.getIsSelected()}
                disabled={!row.getCanSelect()}
                onChange={(checked) => row.toggleSelected(checked)}
              />
            ),
            enableSorting: false,
            size: 40
          }
        ]
      : []),
    ...participantColumns,
    {
      accessorKey: "gender",
      header: "Gender",
      meta: {
        headerClassName: showHeaderInColumn
      },
      enableSorting: false
    },
    {
      accessorKey: "age",
      header: "Age",
      meta: {
        headerClassName: showHeaderInColumn
      },
      enableSorting: false
    },
    attendanceColumn,
    {
      accessorKey: "score_18_1",
      header: "Round 1",
      enableSorting: false,
      cell: (cellProps) => {
        if (!isSetupComplete) {
          return <span className="opacity-20">--</span>;
        }
        return cellProps.getValue();
      },
      meta: {
        className: getStickyColumnClasses(hasRegistrations),
        headerClassName: showHeaderInColumn,
        editable: (row: Row<RegistrantRow>) => !isFinalized && isSetupComplete && row.original.attended !== false,
        onEdit: (value: string, cell: Cell<RegistrantRow, unknown>) => {
          onScoreUpdate?.("roundOneScore", cell.row.original.id, value);
        },
        editableIcon: (cell: Cell<RegistrantRow, unknown>) => {
          const { meets_qualifying_score_18_1 } = cell.row.original;
          return meets_qualifying_score_18_1 && <Icon name="check" />;
        }
      }
    },
    {
      accessorKey: "score_18_2",
      header: "Round 2",
      enableSorting: false,
      cell: (cellProps) => {
        if (!isSetupComplete) {
          return <span className="opacity-20">--</span>;
        }
        return cellProps.getValue();
      },
      meta: {
        className: getStickyColumnClasses(hasRegistrations),
        headerClassName: showHeaderInColumn,
        editable: (row: Row<RegistrantRow>) => !isFinalized && isSetupComplete && row.original.attended !== false,
        onEdit: (value: string, cell: Cell<RegistrantRow, unknown>) => {
          onScoreUpdate?.("roundTwoScore", cell.row.original.id, value);
        },
        editableIcon: (cell: Cell<RegistrantRow, unknown>) => {
          const { meets_qualifying_score_18_2 } = cell.row.original;
          return meets_qualifying_score_18_2 && <Icon name="check" />;
        }
      }
    },
    {
      accessorKey: "score_total",
      header: "Total",
      enableSorting: false,
      meta: {
        className: getStickyColumnClasses(hasRegistrations),
        headerClassName: showHeaderInColumn
      },
      cell: (cellProps) => {
        if (!isSetupComplete) {
          return <span className="opacity-20">--</span>;
        }
        const { score_total } = cellProps.row.original;
        return <span>{score_total || "--"}</span>;
      }
    },
    {
      accessorKey: "registrantStatus",
      header: "Status",
      enableSorting: false,
      size: 180,
      cell: (cellProps) => {
        if (!isSetupComplete) {
          return <span className="opacity-20">--</span>;
        }
        return <RegistrantStatusCell row={cellProps.row.original} />;
      }
    },
    buildIconTextColumn({
      accessorKey: "details",
      header: "Details",
      enableSorting: false,
      meta: { skipEmptyState: true },
      getText: getDetailsColumnText,
      getIcon: (row: RegistrantRow) => getPATDocumentIconConfig(row.hasDocument, row.documentUrl, row.note)
    }),
    buildActionColumn<RegistrantRow>({
      setMenu,
      actionId,
      viewButtonTitle: "View person details",
      onViewClick: (row) => {
        setSelectedPgaId(row.pgaId);
        setIsPersonSideSheetOpen(true);
      },
      onEditClick: isCompactLayout && canEdit && !isFinalized && isSetupComplete ? onEditAttendanceClick : undefined,
      editButtonTitle: "Edit Scores & Attendance",
      getActions: (row) =>
        getRowActions({
          row,
          isSetupComplete,
          canEdit,
          isFinalized,
          setShowEditScoreModal,
          setTargetEventRegistration,
          setUpdateStatusDialogOpen,
          handleStatusUpdate
        })
    })
  ];
};
